/**
 * T-226: the alert summary's settings (superadmin) and each person's own time zone.
 * Server: api/routes/admin/alertDigestRoutes.js and api/routes/admin/meTimeZone.js.
 */
import { adminFetch } from './adminApi';

export type DigestCadence = 'daily' | 'weekly';

export interface DigestSchedule {
  daily: { enabled: boolean; time: string };
  /** day: 0 (Sunday) to 6 (Saturday). */
  weekly: { enabled: boolean; day: number; time: string };
}

export interface DigestSendRow {
  cadence: DigestCadence;
  recipient: string;
  period_end: string;
  status: 'running' | 'sent' | 'skipped' | 'failed';
  reason: string | null;
  attempt_count: number;
  failures: number;
  finished_at: string | null;
}

export interface DigestSettings {
  schedule: DigestSchedule;
  /** Whether the `alert_digest` email route can send (switch on, email set up). */
  route: { key: string; sendable: boolean; reason: string };
  /** Whether non-urgent alert emails are being held for the summary right now, and why not. */
  holding: { active: boolean; reason: string };
  recent: DigestSendRow[];
}

export function getAlertDigestSettings(): Promise<DigestSettings> {
  return adminFetch<DigestSettings>('/alerts/digest/settings');
}

export function saveAlertDigestSchedule(schedule: DigestSchedule): Promise<{ schedule: DigestSchedule }> {
  return adminFetch<{ schedule: DigestSchedule }>('/alerts/digest/settings', { method: 'PUT', body: JSON.stringify(schedule) });
}

/** The last 7 days of real alerts as a summary, to the person who pressed it only. */
export function sendSampleAlertDigest(): Promise<{ ok: boolean; to: string; time_zone: string; counts: Record<string, number> }> {
  return adminFetch('/alerts/digest/sample', { method: 'POST' });
}

export interface MyTimeZone {
  time_zone: string | null;
  /** The zone actually used (Pacific when none is set). */
  effective: string;
  default: string;
}

export function getMyTimeZone(): Promise<MyTimeZone> {
  return adminFetch<MyTimeZone>('/me/time-zone');
}

/** onlyIfUnset: the browser fill at sign-in, which never overwrites a person's choice. */
export function saveMyTimeZone(timeZone: string, opts: { onlyIfUnset?: boolean } = {}): Promise<{ time_zone: string | null }> {
  const body = opts.onlyIfUnset ? { time_zone: timeZone, only_if_unset: true } : { time_zone: timeZone };
  return adminFetch<{ time_zone: string | null }>('/me/time-zone', { method: 'PUT', body: JSON.stringify(body) });
}
